import { resolveApiUrl } from "@/api/client";
import { mapCssAssetUrls } from "./html-css-assets";

const UPLOAD_PATH = "/api/v1/uploads/";
const SPACE = /[\t\n\f\r ]/;
// Skip comments and raw-text elements. Rewriting only real asset attributes
// leaves scripts, styles, text, fragment links and authored formatting intact.
const ASSET_TAG_NAMES = new Set(["img", "source", "video", "audio", "a"]);
const ASSET_TAG =
  /<!--[\s\S]*?(?:-->|$)|<![^>]*>|<(script|style|textarea|title|iframe|noscript|xmp|noembed|noframes)\b[^>]*>[\s\S]*?(?:<\/\1\s*>|$)|<([a-z][a-z0-9:-]*)\b(?:"[^"]*"|'[^']*'|[^'">])*>/gi;
const ATTRIBUTE =
  /(\s+)([^\s"'<>/=]+)(\s*=\s*)(?:"([^"]*)"|'([^']*)'|([^\s"'=<>`]+))/g;

/** Resolve srcset candidates without splitting commas inside data URLs. */
function resolveSrcset(
  value: string,
  resolve: (url: string) => string,
): string {
  let index = 0;
  let copied = 0;
  let result = "";
  while (index < value.length) {
    while (
      index < value.length &&
      (SPACE.test(value[index]!) || value[index] === ",")
    )
      index++;
    const start = index;
    while (index < value.length && !SPACE.test(value[index]!)) index++;
    let end = index;
    while (end > start && value[end - 1] === ",") end--;
    const url = value.slice(start, end);
    const resolved = resolve(url);
    if (resolved !== url) {
      result += value.slice(copied, start) + resolved;
      copied = end;
    }
    if (end < index) continue;
    let parentheses = 0;
    while (index < value.length) {
      const character = value[index++];
      if (character === "(") parentheses++;
      if (character === ")") parentheses = Math.max(0, parentheses - 1);
      if (character === "," && parentheses === 0) break;
    }
  }
  return result + value.slice(copied);
}

/** Uploaded assets are relative to Django, not a bundled tauri:// document.
 * Avoid a global <base>: it also changes normal links and hash navigation. */
export function resolveHtmlUploadAssets(html: string): string {
  const uploadBase = resolveApiUrl(UPLOAD_PATH);
  if (uploadBase === UPLOAD_PATH) return html;
  const resolve = (url: string) =>
    url.startsWith(UPLOAD_PATH)
      ? uploadBase + url.slice(UPLOAD_PATH.length)
      : url;
  return mapHtmlAssetUrls(html, resolve);
}

/** Visit only actual media/link attributes, never scripts or authored text. */
export function mapHtmlAssetUrls(
  html: string,
  resolve: (url: string) => string,
): string {
  let decoder: HTMLTextAreaElement | undefined;
  const decode = (value: string) => {
    if (!value.includes("&")) return value;
    decoder ??= document.createElement("textarea");
    decoder.innerHTML = value.replace(/</g, "&lt;");
    return decoder.value;
  };
  return html.replace(
    ASSET_TAG,
    (tag, rawText, tagName: string | undefined) => {
      if (rawText?.toLowerCase() === "style")
        return tag.replace(
          /^(<style\b[^>]*>)([\s\S]*?)(<\/style\s*>|$)/i,
          (_whole: string, open: string, css: string, close: string) =>
            open + mapCssAssetUrls(css, resolve) + close,
        );
      if (!tagName) return tag;
      const name = tagName.toLowerCase();
      return tag.replace(
        ATTRIBUTE,
        (attribute, space, key, equals, double, single, bare) => {
          const attributeName = (key as string).toLowerCase();
          const media = name !== "a";
          const css = attributeName === "style";
          if (
            !(
              css ||
              (ASSET_TAG_NAMES.has(name) && media && attributeName === "src") ||
              ((name === "img" || name === "source") &&
                attributeName === "srcset") ||
              (name === "video" && attributeName === "poster") ||
              (name === "a" && attributeName === "href")
            )
          )
            return attribute;
          const value = decode((double ?? single ?? bare) as string);
          const resolved = css
            ? mapCssAssetUrls(value, resolve)
            : attributeName === "srcset"
              ? resolveSrcset(value, resolve)
              : resolve(value);
          if (resolved === value) return attribute;
          const quote =
            double !== undefined ? '"' : single !== undefined ? "'" : "";
          const encoded = resolved
            .replace(/&/g, "&amp;")
            .replace(/</g, "&lt;")
            .replace(/>/g, "&gt;")
            .replace(
              quote === '"' ? /"/g : /'/g,
              quote === '"' ? "&quot;" : "&#39;",
            );
          const safe = quote
            ? encoded
            : encoded.replace(
                /[\s"`=]/g,
                (character) => `&#${character.charCodeAt(0)};`,
              );
          return `${space}${key}${equals}${quote}${safe}${quote}`;
        },
      );
    },
  );
}

export function htmlAssetUrls(html: string): string[] {
  const urls = new Set<string>();
  mapHtmlAssetUrls(html, (url) => {
    urls.add(url);
    return url;
  });
  return [...urls];
}
